import React, {
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import Accordion from '../../../../../components/Accordion';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import Lead from '../../../../Model/Lead';
import User from '../../../../../Model/User';
import PostPaymentFormAnswer from '../../../../Model/PostPaymentFormAnswer';
import LoadingPage from '../../../../../components/LoadingPage';
import EmptyView from '../EmptyView';

import {
  Container,
} from './styles';
import texts from './texts.json';
import FormResponseContainer from './FormResponseContainer';

export const AccordionId = {
  NONE: null,
  LEAD_FORM_ACCORDION: 'leadFormAccordion',
  POST_PAYMENT_ACCORDION: 'postPaymentAccordion',
};

const ClientInfo = ({
  userDoc,
  coachId,
  defaultExpandedAccordion,
}) => {
  const [leadDoc, setLeadDoc] = useState(null);
  const [postPaymentFormDoc, setPostPaymentFormDoc] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpandedAccordion);

  const isComponentMountedRef = useComponentMounted();

  // Allow only one accordion to be open at a time
  const handleExpandForm = (accordion) => (event, isExpanded) => {
    setExpanded(isExpanded ? accordion : AccordionId.NONE);
  };

  useEffect(() => {
    // Both forms belong to one relationship, so a coach switch has to re-arm the gate and
    // drop what is on screen: assigning only a truthy result would leave the previous
    // coach's forms up when this coach has none.
    setIsReady(false);
    setLeadDoc(null);
    setPostPaymentFormDoc(null);

    const init = async () => {
      const {
        id: userId,
        email,
      } = userDoc;
      const userLeadDoc = await Lead.getLeadByEmail(email, coachId);
      const userPostPaymentFormDoc = await PostPaymentFormAnswer.getLatestByUser(userId, coachId);

      if (isComponentMountedRef.current) {
        setLeadDoc(userLeadDoc);
        setPostPaymentFormDoc(userPostPaymentFormDoc);
        setIsReady(true);
      }
    };

    init();
  }, [
    isComponentMountedRef,
    userDoc,
    coachId,
  ]);

  if (!isReady) {
    return <LoadingPage />;
  }

  if (!leadDoc && !postPaymentFormDoc) {
    return <EmptyView text={texts.emptyText} />;
  }

  return (
    <Container>
      {!!leadDoc && (
        <Accordion
          key={AccordionId.LEAD_FORM_ACCORDION}
          title={texts.LeadFormQuestions}
          expanded={expanded === AccordionId.LEAD_FORM_ACCORDION}
          onChange={handleExpandForm(AccordionId.LEAD_FORM_ACCORDION)}
        >
          <FormResponseContainer formDoc={leadDoc} clientUserDoc={userDoc} />
        </Accordion>
      )}
      {!!postPaymentFormDoc && (
        <Accordion
          key={AccordionId.POST_PAYMENT_ACCORDION}
          title={texts.PostPaymentFormQuestions}
          expanded={expanded === AccordionId.POST_PAYMENT_ACCORDION}
          onChange={handleExpandForm(AccordionId.POST_PAYMENT_ACCORDION)}
        >
          <FormResponseContainer formDoc={postPaymentFormDoc} clientUserDoc={userDoc} />
        </Accordion>
      )}
    </Container>
  );
};

ClientInfo.propTypes = {
  userDoc: PropTypes.instanceOf(User),
  coachId: PropTypes.string,
  defaultExpandedAccordion: PropTypes.oneOf([
    AccordionId.NONE,
    AccordionId.LEAD_FORM_ACCORDION,
    AccordionId.POST_PAYMENT_ACCORDION,
  ]),
};

ClientInfo.defaultProps = {
  userDoc: null,
  coachId: '',
  defaultExpandedAccordion: AccordionId.NONE,
};

export default compose(
  observer,
)(ClientInfo);
